<template>
  <div v-if="populateDataSuccess">
    <div :class="$style.applyWidth">
      <div :class="$style.siteFilterContainer">
        <SiteFilter
          v-if="siteList.length > 1"
          :auditSites="siteList"
          :filteredSites="filteredSites"
          :isDisabled="reportLoading"
          @applySiteFilter="applySiteFilter"
        />
        <b-field label="Site" v-else>
          <b-input
            :value="siteList[0].name"
            disabled
            :custom-class="$style.singleSiteFieldInput"
          ></b-input>
        </b-field>
        <div
          :class="$style.individualSiteCheckbox"
          v-if="siteList.length > 1 && this.filteredSites.length > 1"
        >
          <b-checkbox
            v-model="checkboxIndividualSite"
            @input="showAnalysisResult"
            class="checkbox-size"
            :disabled="reportLoading"
            >Individual</b-checkbox
          >
        </div>
      </div>
      <div :class="$style.criteriaFilterContainer">
        <CriteriaFilter
          v-if="criteriaList.length > 0"
          :auditCriteria="criteriaList"
          :filteredCriterias="filteredCriterias"
          :isDisabled="reportLoading"
          @applyCriteriaFilter="applyCriteriaFilter"
        />
        <div :class="$style.individualCriteriaCheckbox">
          <b-checkbox
            v-if="populateDataSuccess"
            v-model="checkboxIndividualCriteria"
            @input="showAnalysisResult"
            class="checkbox-size"
            :disabled="reportLoading"
            >Individual</b-checkbox
          >
        </div>
      </div>
      <div :class="$style.chartTypeContainer">
        <ChartTypeFilter
          v-if="populateDataSuccess"
          :selectedChartType="selectedChartType"
          :isDisabled="reportLoading"
          @applyChartType="applyChartType"
        />
      </div>
    </div>
    <div :class="$style.comparisonContainer" v-if="showComparisonContainer">
      <div :class="[$style.comparisonGrid, 'columns', 'is-multiline']">
        <div
          v-for="(period, periodIndex) in comparisonPeriods"
          :key="periodIndex"
          :class="[
            $style.pickerContainer,
            'column',
            'is-5',
            periodIndex % 2 === 0
              ? $style.unsetRightPadding
              : $style.unsetLeftPadding,
          ]"
        >
          <AnalysisComparisonPeriodPicker
            :selectedPeriod="period"
            :periodIndex="periodIndex"
            :isDisabled="reportLoading"
            @applyAnalysisComparisonPeriod="applyAnalysisComparisonPeriod"
            @removePeriod="handleRemovePeriod"
          />
        </div>
        <div :class="[$style.unsetLeftPadding, 'column', 'is-2']">
          <button
            @click="addNewPeriod()"
            :class="[
              $style.addComparisonButton,
              reportLoading ? $style.disableColor : null,
            ]"
          >
            +Add Comparison
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="showSinglePeriodContainer"
      :class="[
        $style.applyPeriod,
        'analysis-period-picker-container',
        'columns',
      ]"
    >
      <div class="column">
        <AnalysisPeriodPicker
          :selectedPeriod="getSelectedPeriod"
          :isDisabled="reportLoading"
          @applyAnalysisPeriod="applyAnalysisPeriod"
        />
      </div>
      <div :class="[$style.applyPeriodPadding, 'column']">
        <button
          @click="addNewPeriod()"
          :class="[
            $style.addComparisonButton,
            reportLoading ? $style.disableColor : null,
          ]"
          :disabled="reportLoading"
        >
          +Add Comparison
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { cloneDeep as _cloneDeep, map as _map } from 'lodash';
import { Action, State } from 'vuex-class';
import SiteFilter from '@/views/Audit/components/SiteFilter.vue';
import CriteriaFilter from '@/views/Audit/components/CriteriaFilter.vue';
import ChartTypeFilter from '@/views/Audit/components/ChartTypeFilter.vue';
import AnalysisPeriodPicker from '@/views/Audit/components/AnalysisPeriodPicker.vue';
import AnalysisComparisonPeriodPicker from '@/views/Audit/components/AnalysisComparisonPeriodPicker.vue';
import {
  AuditSite,
  AuditCriteria,
  ChartType,
  AuditReportFiltersConfig,
} from '@/store/modules/audits/types/audits.types';

@Component({
  components: {
    SiteFilter,
    CriteriaFilter,
    ChartTypeFilter,
    AnalysisPeriodPicker,
    AnalysisComparisonPeriodPicker,
  },
})
export default class AuditReportFilters extends Vue {
  @Prop()
  public auditSites!: AuditSite[];
  @Prop()
  public auditCriteria!: AuditCriteria[];
  @Prop()
  public analysisFilter!: AuditReportFiltersConfig;
  @Prop()
  public reportLoading!: boolean;

  public filteredSites: number[] = [];
  public filteredCriterias: number[] = [];
  public selectedChartType: string = ChartType.verticalBarChart;
  public todayDate: Date = this.setTimeEnd(new Date());
  public basePeriod: Date[] = [
    new Date(
      this.todayDate.getFullYear(),
      this.todayDate.getMonth(),
      this.todayDate.getDate() - 7,
    ),
    this.todayDate,
  ];
  public selectedPeriod: Date[] = [];
  public checkboxIndividualSite: boolean = false;
  public checkboxIndividualCriteria: boolean = false;
  public showComparisonContainer: boolean = false;
  public showSinglePeriodContainer: boolean = true;
  public comparisonPeriods: Date[][] = [];
  public comparisonPeriodsKey: boolean = true;
  public populateDataSuccess: boolean = false;

  public mounted() {
    this.populateData();
  }

  public populateData() {
    this.filteredSites = _cloneDeep(this.analysisFilter.filteredSites);
    this.filteredCriterias = _cloneDeep(this.analysisFilter.filteredCriterias);
    this.selectedChartType = this.analysisFilter.selectedChartType;
    this.checkboxIndividualSite = this.analysisFilter.checkIndividualSite;
    this.checkboxIndividualCriteria = this.analysisFilter.checkIndividualCriteria;
    this.selectedPeriod = _cloneDeep(this.analysisFilter.selectedPeriod);
    this.showComparisonContainer = this.analysisFilter.checkComparison;
    this.comparisonPeriods = _cloneDeep(this.analysisFilter.comparisonPeriods);
    if (this.showComparisonContainer) {
      this.showSinglePeriodContainer = false;
    }
    this.populateDataSuccess = true;
  }

  get siteList(): any[] {
    const siteArray: any[] = [];
    _map(this.auditSites, (auditSite) => {
      siteArray.push({
        siteId: auditSite.site.id,
        name: auditSite.site.name,
        auditSiteId: auditSite.id,
      });
    });
    return siteArray;
  }

  get criteriaList(): AuditCriteria[] {
    return this.auditCriteria;
  }

  public applySiteFilter(newValue: number[]) {
    this.filteredSites = newValue;
    this.showAnalysisResult();
  }

  public applyCriteriaFilter(newValue: number[]) {
    this.filteredCriterias = newValue;
    this.showAnalysisResult();
  }

  public applyChartType(newValue: ChartType) {
    this.selectedChartType = newValue;
    this.showAnalysisResult();
  }

  public applyAnalysisPeriod(newValue: Date[]) {
    this.selectedPeriod = newValue;
    this.showAnalysisResult();
  }

  public addNewPeriod() {
    this.showComparisonContainer = true;
    this.showSinglePeriodContainer = false;
    if (this.comparisonPeriods.length === 1) {
      this.comparisonPeriods = _cloneDeep([
        this.selectedPeriod,
        this.basePeriod,
      ]);
    } else {
      this.comparisonPeriods.push(this.basePeriod);
    }
    this.showAnalysisResult();
  }

  public handleRemovePeriod(comparisonPeriodIndex: number) {
    this.comparisonPeriods.splice(comparisonPeriodIndex, 1);
    if (this.comparisonPeriods.length === 1) {
      this.selectedPeriod = _cloneDeep(this.comparisonPeriods[0]);
      this.showComparisonContainer = false;
      this.showSinglePeriodContainer = true;
    }
    this.showAnalysisResult();
  }

  public applyAnalysisComparisonPeriod(newValue: {
    period: Date[];
    periodIndex: number;
  }) {
    this.comparisonPeriods[newValue.periodIndex] = _cloneDeep(newValue.period);
    this.showAnalysisResult();
  }

  public showAnalysisResult() {
    const filters: AuditReportFiltersConfig = {
      filteredSites: this.filteredSites,
      filteredCriterias: this.filteredCriterias,
      selectedChartType: this.selectedChartType,
      selectedPeriod: this.selectedPeriod,
      checkIndividualSite: this.checkboxIndividualSite,
      checkIndividualCriteria: this.checkboxIndividualCriteria,
      checkComparison: this.showComparisonContainer,
      comparisonPeriods: this.comparisonPeriods,
    };
    this.$emit('applyAnalysisFilters', filters);
  }

  public setTimeEnd(date: Date): Date {
    date.setHours(23);
    date.setMinutes(59);
    date.setSeconds(59);
    return date;
  }

  public setTimeStart(date: Date): Date {
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    return date;
  }

  get getSelectedPeriod(): Date[] {
    return this.selectedPeriod;
  }
}
</script>
<style lang="scss" module>
.applyWidth {
  width: 512px;
  div > div > div {
    width: 512px;
  }
}
.siteFilterContainer {
  display: flex;
  flex-direction: row;
  margin-top: 4px;
}
.criteriaFilterContainer {
  display: flex;
  flex-direction: row;
  margin-top: 16px;
}
.chartTypeContainer {
  margin-top: 16px;
}
.applyPeriod {
  margin-top: 16px;
  width: 248px;
  width: 810px;
}
.showResultButtonContainer {
  margin-top: 24px;
}
.showResultButton {
  padding: 12px 20px 12px 20px;
}
.individualSiteCheckbox {
  padding-top: 34px;
  margin-left: 16px;
}
.individualCriteriaCheckbox {
  padding-top: 34px;
  margin-left: 16px;
}
.singleSiteFieldInput {
  background-color: white !important;
  border: 1px solid #dfdfdf;
  border-color: #dfdfdf !important;
  color: #9f9f9f !important;
  padding-top: 10px;
  padding-bottom: 12px;
  padding-left: 12px;
}
.addComparisonButton {
  font-size: 14px;
  font-weight: bold;
  color: #00205b;
  margin-top: 34px;
  cursor: default;
  background: unset;
  border: unset;
  padding-left: 0px;
  padding-right: 0px;
}
.pickerContainer {
  width: 400px !important;
  margin-right: 16px;
  padding-right: 0px;
}
.unsetRightPadding {
  padding-right: 0px;
}
.unsetLeftPadding {
  padding-left: 0px;
}
.comparisonContainer {
  display: flex;
  flex-direction: row;
  max-width: 100%;
  width: 70rem;
}
.applyPeriodPadding {
  padding-left: 22px;
}
.comparisonGrid {
  margin-top: 16px;
}
.setButtonMargin {
  margin-top: 0px;
}
.disableColor {
  color: #ececec;
}
</style>
